"use client"

import { useState } from "react"
import { GlowCard } from "@/components/crystality/glow-card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Loader2, Mail, ArrowLeft, CheckCircle } from "lucide-react"
import Link from "next/link"

export function ForgotPasswordForm() {
  const [email, setEmail] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email) {
      setError("Please enter your email address")
      return
    }

    setError("")
    setIsLoading(true)

    try {
      const response = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      })

      const data = await response.json()

      if (!response.ok) {
        setError(data.error || "Something went wrong")
        return
      }

      setSuccess(true)
    } catch {
      setError("Failed to connect to server. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  if (success) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Check Your Email</h1>
          <p className="mt-2 text-muted-foreground">
            We sent a password reset link to your email
          </p>
        </div>

        <GlowCard variant="subtle" className="p-6">
          <div className="text-center space-y-4">
            <CheckCircle className="h-12 w-12 text-green-400 mx-auto" />
            <p className="text-sm text-muted-foreground">
              If an account exists with <span className="text-foreground font-medium">{email}</span>, you will receive an email with a link to reset your password. The link expires in 1 hour.
            </p>
            <p className="text-xs text-muted-foreground">
              {"Didn't receive it? Check your spam folder or try again."}
            </p>
          </div>
        </GlowCard>

        <div className="flex flex-col gap-2">
          <Button variant="outline" className="w-full" onClick={() => { setSuccess(false); setEmail("") }}>
            Try a different email
          </Button>
          <Link href="/sign-in">
            <Button variant="ghost" className="w-full">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Sign In
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold">Forgot Password</h1>
        <p className="mt-2 text-muted-foreground">
          Enter your email and we will send you a reset link
        </p>
      </div>

      <GlowCard variant="subtle" className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-secondary/50 border-border focus:border-primary"
              disabled={isLoading}
            />
          </div>

          {error && (
            <p className="text-sm text-destructive text-center">{error}</p>
          )}

          <Button
            type="submit"
            className="w-full h-11 gradient-purple hover:opacity-90 transition-opacity text-white font-medium"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="mr-2 h-4 w-4" />
                Send Reset Link
              </>
            )}
          </Button>
        </form>
      </GlowCard>

      <Link href="/sign-in">
        <Button variant="ghost" className="w-full">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Sign In
        </Button>
      </Link>
    </div>
  )
}
